import { Injectable, Injector } from '@angular/core';
import { User, UserInfo } from '../../entities/User';
import { Credentials } from '../../entities/Credentials';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, retry, Subject, throwError } from 'rxjs';
import { BaseServicesService } from './base.service';
import { Router } from '@angular/router';
import { CambiarPassword } from 'src/app/entities/cambiarPassword';
import { ResetPassword } from './ResetPassword';

@Injectable({ providedIn: 'root' })
export class AuthenticationService extends BaseServicesService {
  private authenticationChanged = new Subject<boolean>();
  private user = new User();

   private imagenPerfilSubject = new BehaviorSubject<string>(
    this.getUser() && this.getUser().user ? this.getUser().user.imagenPerfil : ''
  );
  
   public imagenPerfil$ = this.imagenPerfilSubject.asObservable();
 
   updateImagenPerfil(newImageUrl: string) {
     const currentUser = this.getUser();
     currentUser.user.imagenPerfil = newImageUrl;
     sessionStorage.setItem('user', JSON.stringify(currentUser));
     this.imagenPerfilSubject.next(newImageUrl);
   }

   canActivate(): boolean {
    const authService = this.injector.get(AuthenticationService);
    if (authService.isAuthenticated()) {
      return true;
    } else {
      this.router.navigate(['/login']);
      return false;
    }
  }

  constructor(
    private injector: Injector,
    private http: HttpClient,
    private router: Router,
  ) {
    super();
  }

  public isAuthenticated(): boolean {
    return !(
      sessionStorage.getItem('token') === undefined ||
      sessionStorage.getItem('token') === null ||
      sessionStorage.getItem('token') === 'null' ||
      sessionStorage.getItem('token') === 'undefined' ||
      sessionStorage.getItem('token') === ''
    );
  }

  public isAuthenticationChanged(): any {
    return this.authenticationChanged.asObservable();
  }

  clearUserData() {
    this.user = null;
    sessionStorage.clear();
    this.authenticationChanged.next(false);
  }

  public getToken(): any {
    if (
      sessionStorage.getItem("token") === undefined ||
      sessionStorage.getItem("token") === null ||
      sessionStorage.getItem("token") === "null" ||
      sessionStorage.getItem("token") === "undefined" ||
      sessionStorage.getItem("token") === ""
    ) {
      return "";
    }
    return JSON.parse(sessionStorage.getItem("token"));
  }

  public setData(data: User): void {    
    this.setStorageToken(data.token);
    this.setStorageUser(data);
    this.setStoragePermissions(data.permisos);
  }

  public failToken(): void {
    this.cleanSession();
  }

  public logout(): void {
    this.cleanSession();
    this.router.navigate(['/login']);
  }

  private setStorageToken(value: any): void {
    let _value = JSON.stringify(value);
    sessionStorage.setItem("token", _value);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public changePassword(passwords: CambiarPassword): Observable<boolean> {
		return this.http.put<boolean>(environment.API_SECURITY + '/api/Authentication/CambiarPassword', passwords);
	}

  changePasswordPerfil(passwords: any): Observable<string> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/update/password`, passwords, {
        responseType: 'text'
    }).pipe(
        catchError((err: HttpErrorResponse) => {
            let errorMessage = 'Ocurrió un error al actualizar la contraseña.';
            if (err.status === HttpStatusCode.BadRequest) {
                errorMessage = 'Contraseña actual incorrecta.';
            } else if (err.status === HttpStatusCode.NotFound) {
                errorMessage = 'El recurso solicitado no fue encontrado.';
            } else if (err.status === HttpStatusCode.InternalServerError) {
                errorMessage = 'Ocurrió un error en el servidor.';
            }
            return throwError(errorMessage);
        })
    );
}


  public solicitarCambioPasswordByEmail(email: string): Observable<any> {
    return this.http.post<any>(`${environment.API_SECURITY}/usuarios/forgot-password`, { email });
  }
  
  public resetPassword(body: any): Observable<string> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/updateForgotPassword`, body, {
        responseType: 'text' // Especifica que la respuesta debe tratarse como texto
    }).pipe(
        catchError((err: HttpErrorResponse) => {
            let errorMessage = 'Ocurrió un error al actualizar la contraseña.';
            if (err.status === HttpStatusCode.Forbidden) {
                errorMessage = 'No tiene permisos para realizar esta acción.';
            } else if (err.status === HttpStatusCode.NotFound) {
                errorMessage = 'El recurso solicitado no fue encontrado.';
            } else if (err.status === HttpStatusCode.InternalServerError) {
                errorMessage = 'Ocurrió un error en el servidor.';
            }
            return throwError(errorMessage);
        })
    );
}


  image(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.put(`${environment.API_SECURITY}/usuarios/update/imageProfile`, formData, {
      responseType: 'text' // Especifica que la respuesta debe tratarse como texto
    });
  }

  // Método para actualizar la imagen de perfil
  actualizarImagenPerfil(data: { imagenPerfil: string }): Observable<any> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/actualizar-imagen-perfil`, data);
  }

  private setStorageUser(value: any): void {
    let _value = JSON.stringify(value);
    sessionStorage.setItem("user", _value);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public setStorageCoordinate(coordinates): void {
    let coords = JSON.stringify(coordinates);
    sessionStorage.setItem("coordinates", coords);
  }

  updateUsuario(id: string, form: any): Observable<any> {
    return this.http.put<any>(
      `${environment.API_SECURITY}/controlusuarios/${id}`,
      form
    ).pipe(catchError(this.handleError));
  }

  getUsuarioControl(id: string): Observable<any> {
    return this.http.get<any>(
      `${environment.API_SECURITY}/controlusuarios/${id}`
    ).pipe(catchError(this.handleError));
  }

  private setStoragePermissions(permissions: Array<any>): void {
    const transformedPermissions = permissions.map(permission => permission.idPermiso);
    let _value = JSON.stringify(transformedPermissions);
    // console.log(_value);
    sessionStorage.setItem("permissions", _value);
    // console.log('Permisos establecidos en storage:', permissions);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public cleanSession() {
    sessionStorage.clear();
  }

  public getUser(): User {
    const user = sessionStorage.getItem("user");
    // console.log(user);
    
    if (user) {
      return JSON.parse(user);
    } else {
      return new User();
    }
  }
  

  public getCoordinates() {
    return JSON.parse(sessionStorage.getItem("coordinates"));
  }

  public getPermissions(): string[] {
    return JSON.parse(sessionStorage.getItem("permissions"));
  }

  public authenticate(body: Credentials): Observable<User> {
    return this.http.post<User>(environment.API_SECURITY + "/auth", body)
      .pipe(catchError(this.handleError));
  }
}
